import React, { useState, useEffect } from "react";
import { Button, Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { messages } from "../views/Job/Job.messages";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { far } from "@fortawesome/pro-regular-svg-icons";
import { default as dashBoardMessages } from "../views/Dashboard/Dashboard.messages";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import MandateModal from "../views/Job/components/Modals/MandateModal";
import { jobFetchJob } from "../services/job";
import useJob from "../hooks/useJob";
import { toggleLoading } from "../services/helpers";
import * as Sentry from "@sentry/react";
import OverpaidWizard from "./OverpaidWizard";
import useFormatter from "../hooks/useFormatter";

const JobAdditionalActions = (props) => {
    const [showMandateModal, setShowMandateModal] = useState(false);
    const [mandateAlertDialog, setMandateAlertDialog] = useState({});
    const { formatCurrency } = useFormatter();
    const { submitMandate, downloadJobPdf } = useJob(props.job);

    const showMandate = props.job.statuscode !== 101 && props.job.statuscode !== 122;
    const lng = props.intl.formatMessage;

    useEffect(() => {
        // Show overpaid info if job is overpaid and bank account has been notified
        if ((props.job.statuscode === 302 || props.job.statuscode === 104) && props.job.modoverpaid !== true) {
            const info = resolveOverpaidInformationFromJob(props.job);
            props.onOverpaidInfo(info);
        }
    }, [props.job]);

    const toggleMandateModal = () => {
        setShowMandateModal(!showMandateModal);
    }

    const toggleOverpaidWizard = () => {
        props.setShowOverpaidWizard(!props.showOverpaidWizard);
    }

    /**
     * Resolve overpaid information from job data
     * @param {object} job
     * @returns {object}
     */
    const resolveOverpaidInformationFromJob = (job) => {
        // Get locale and currency for formatting
        const currency = job.currency !== '' ? job.currency : process.env.REACT_APP_DEFAULT_CURRENCY;
        return {
            show: true,
            account: job.overpaid_iban,
            amount: formatCurrency(job.modoverpaidamount, currency),
            name: job.overpaid_name,
            fee: formatCurrency(job.modoverpaidfee, currency)
        };
    }

    /**
     * Get overpaid information for job with notified account number
     * and set it to be displayed to user
     * @param {string} dpid
     * @return {object}
     */
    const handleBankAccountSubmitSuccess = async (dpid) => {
        const job = await jobFetchJob(dpid);
        const info = resolveOverpaidInformationFromJob(job);
        props.onOverpaidInfo(info);
    }
    
    /** Label changes if there are multiple members
     *
     * @type {string|*}
     */
    const invoiceLabel = Array.isArray(props.job.members) && props.job.members.length > 0
        ? `${lng(messages.invoiceImages)} (${props.job.members.length + 1})`
        : lng(messages.invoiceImage);

    const handleSubmitMandate = async (authorizedPerson) => {
        props.toggleLoading(true, lng(messages.actionSaving));
        try {
            await submitMandate(authorizedPerson)
            // A successful request, gets the job's data again
            await jobFetchJob(props.job.dpid);
            props.toggleLoading(false);
            // close mandate modal and show success message in job page
            toggleMandateModal();
            props.handleAlertDialog(lng(messages.titleThankYou) + ". " + lng(messages.changesSaved), 'success');
        } catch (error) {
            props.toggleLoading(false);
            // show alert in mandatemodal without closing it
            setMandateAlertDialog({
                message: lng(messages.titleSavingFailed) + ". " + lng(messages.pleaseTryAgain),
                type: 'danger',
                show: true
            });
        }
    }

    const downloadPdf = (letterType) => {
        if ((props.job.downloadpdf && letterType === 'invoice') ||
            (props.job.current_letter?.downloadpdf && letterType === 'current')) {

            if (letterType === 'invoice') {
                toggleLoading(true, lng(messages.openingInvoice) + "...");
            } else {
                toggleLoading(true, lng(messages.openingCurrentLetter) + "...");
                // Set disclaimer to alert about letter
                props.handleAlertDialog(lng(messages.letterDisclaimer), 'warning');
            }

            downloadJobPdf(letterType).catch(error => {
                Sentry.captureException(error);
                props.handleAlertDialog(lng(messages.titlePdfDownloadFailed) + '. ' + lng(messages.pleaseTryAgain), 'danger');
            }).finally(() => {
                toggleLoading(false);
            });
        }
    }

    return (
        <Card className='fullwidth-mobile grey-bottom-border-mobile'>
            <Card.Body>
                <h3 id="invoice-actions-title">{lng(messages.actions)}</h3>
                <ul className='list-unstyled job-actions'>
                    {props.showWizardButton && (
                        <li>
                            <span className='label'>
                                <Button
                                    className='show-mandate actionlist-button'
                                    variant='tertiary'
                                    onClick={toggleOverpaidWizard}
                                >
                                    <FontAwesomeIcon icon={far.faMoneyCheckPen} />
                                    {lng(dashBoardMessages.returnPaymentTitle)}
                                    <span className='float-end'><FontAwesomeIcon icon={far.faChevronRight} /></span>
                                </Button>
                            </span>
                        </li>
                    )}
                    {showMandate && (
                        <li>
                            <span className='label'>
                                <Button
                                    className='show-mandate actionlist-button'
                                    variant='tertiary'
                                    onClick={toggleMandateModal}
                                    aria-label={lng(messages.mandate)}
                                >
                                    <FontAwesomeIcon
                                        icon={far.faFileSignature}
                                        aria-hidden="true"
                                    />
                                    {lng(messages.mandate)}
                                    <span className='float-end' aria-hidden="true">
                                        <FontAwesomeIcon icon={far.faChevronRight} />
                                    </span>
                                </Button>
                            </span>
                        </li>
                    )}
                    <li>
                        <span className='label'>
                            <OverlayTrigger
                                key='download-pdf'
                                trigger={['hover', 'click']}
                                overlay={
                                    !props.job.downloadpdf ? (
                                        <Tooltip id='pdf-cant-be-loaded' className='danger'>
                                            {props.job.cid === 1046233
                                                ? lng(messages.pdfCantBeLoadedCustom)
                                                : lng(messages.pdfCantBeLoaded)}
                                        </Tooltip>
                                    ) : (<></>)
                                }>
                                <Button
                                    id="download-pdf"
                                    className={props.job.downloadpdf ? 'download-pdf actionlist-button' : 'download-pdf listbutton-disabled actionlist-button'}
                                    variant='tertiary'
                                    onClick={() => downloadPdf('invoice')}
                                    aria-label={lng(messages.invoiceImage)}
                                >
                                    <FontAwesomeIcon
                                        icon={far.faDownload}
                                        aria-hidden="true"
                                    />
                                    {invoiceLabel}
                                    <span className='float-end' aria-hidden="true">
                                        <FontAwesomeIcon icon={far.faChevronRight} />
                                    </span>
                                </Button>
                            </OverlayTrigger>
                        </span>
                    </li>
                    {props.job.current_letter?.downloadpdf && (
                        <li>
                            <span className='label'>
                                <Button
                                    id="download-pdf"
                                    className='download-pdf actionlist-button'
                                    variant='tertiary'
                                    onClick={() => downloadPdf('current')}
                                    aria-label={lng(messages.invoiceCurrentImage)}
                                >
                                    <FontAwesomeIcon icon={far.faDownload} aria-hidden="true" />
                                    {lng(messages.invoiceCurrentImage)}
                                    <span className='float-end' aria-hidden="true">
                                        <FontAwesomeIcon icon={far.faChevronRight} />
                                    </span>
                                </Button>
                            </span>
                        </li>
                    )}
                    {props.showServing && (
                        <li>
                            <span className='label'>
                                <OverlayTrigger
                                    key='serving-button'
                                    placement='top'
                                    trigger={['hover', 'click']}
                                    overlay={
                                        props.servingButtonDisabled ? (
                                            <Tooltip id={`serving-needs-authentication`} className='danger'>
                                                {lng(messages.strongAuthenticationNeeded)}
                                            </Tooltip>
                                        ) : (<></>)
                                    }
                                >
                                    <Button
                                        id='serving-button'
                                        className={props.servingButtonDisabled ? 'sign-serving listbutton-disabled actionlist-button' : 'sign-serving actionlist-button'}
                                        variant='tertiary'
                                        onClick={props.onShowServingModal}
                                    >
                                        <FontAwesomeIcon icon={far.faCircleExclamation} />
                                        {lng(messages.signServing)}
                                        <span className='float-end'>
                                            <FontAwesomeIcon icon={far.faChevronRight} />
                                        </span>
                                    </Button>
                                </OverlayTrigger>
                            </span>
                        </li>
                    )}
                </ul>
                <MandateModal
                    show={showMandateModal}
                    hide={toggleMandateModal}
                    handleSubmit={handleSubmitMandate}
                    alert={mandateAlertDialog}
                />
                {props.isDirectLinkOverpaid && (
                    <OverpaidWizard
                        open={props.showOverpaidWizard}
                        job={props.job}
                        onClose={() => props.setShowOverpaidWizard(false)}
                        showSearchStep={false}
                        onBankAccountSubmitSuccess={handleBankAccountSubmitSuccess}
                    />
                )}
            </Card.Body>
        </Card>
    )
}

JobAdditionalActions.propTypes = {
    intl: PropTypes.object.isRequired,
    showWizardButton: PropTypes.bool,
    showServing: PropTypes.bool,
    servingButtonDisabled: PropTypes.bool,
    onShowServingModal: PropTypes.func,
    job: PropTypes.object,
    toggleLoading: PropTypes.func,
    handleAlertDialog: PropTypes.func,
    isDirectLinkOverpaid: PropTypes.bool,
    onOverpaidInfo: PropTypes.func,
    showOverpaidWizard: PropTypes.bool,
    setShowOverpaidWizard: PropTypes.func
};

export default injectIntl(JobAdditionalActions);
